import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Card, EmptyState, PageHeader, Stat, Tone, WaitingInline } from '~/components/ui';
import TermsCheckbox from '~/components/TermsCheckbox';
import { intlLocale, useLocale, useT } from '~/i18n';
import { formatDate } from '~/lib/date';
import { PARTNER_TERMS_VERSION } from '~/lib/termsVersions';
import { acceptPartnerTerms, applyPartner, loadPartner, requestPayoutsLink } from './api/partnerClient';
import {
  REFERRAL_STATES,
  cleanPartnerCode,
  formatUsdCents,
  hasPartnerPanel,
  initialForm,
  needsPartnerTermsAcceptance,
  invalidPartnerFields,
  partnerCountries,
  sortCountriesByName,
  summarizeReferrals,
  visibleReferrals,
  type PartnerField,
  type PartnerForm,
  type PartnerReferral,
  type PartnerResponse,
  type ReferralFilter,
  type ReferralState,
  type ReferralSummary,
} from './lib/partner';

/**
 * El Programa de socios: quien recomienda Job Apply con su código y cobra USD 5
 * por cada mes que paga cada persona que trajo. NO es la consola de agencias
 * (`/agency`), que es para empresas que publican vacantes.
 *
 * Una sola pantalla para las cuatro etapas, porque es UNA relación que avanza:
 *
 *   sin solicitud  → cómo funciona + el formulario
 *   PENDING        → "recibimos tu solicitud"
 *   REJECTED       → el motivo + el formulario para reenviarla
 *   ACTIVE/PAUSED  → el panel: código, enlace, cobros, referidos, comisiones
 *   TERMINATED     → el panel sin enlace ni referidos: lo causado se le paga
 *
 * Desde 2026-10-08 la solicitud lleva una casilla OBLIGATORIA de los Términos
 * del Programa, y el panel pide aceptar la versión vigente a quien no la aceptó.
 *
 * Vive en dos direcciones: `/perfil/socios` (una tab más del perfil, la entrada
 * visible para quien ya es candidato) y `/socios` suelta (`standalone`), que
 * es la que se comparte y a la que vuelve Stripe. La suelta se abre aunque el
 * alta de candidato esté a medias: un socio puede no postular a nada.
 */
export default function JobApplyPartnerPage({ standalone = false }: { standalone?: boolean }) {
  const t = useT('partner');
  const locale = useLocale();
  const [data, setData] = useState<PartnerResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const alive = useRef(true);

  const load = useCallback(async () => {
    const result = await loadPartner();
    if (!alive.current) return;
    if (result.status === 'ok') {
      setData(result.data);
      setState('ready');
    } else {
      setState('failed');
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => { alive.current = false; };
  }, [load]);

  const countryName = useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([intlLocale(locale)], { type: 'region' });
    } catch {
      names = null;
    }
    return (code: string) => names?.of(code) ?? code;
  }, [locale]);

  // La lista del servidor (la que va a aceptar la solicitud), por nombre.
  const countries = useMemo(
    () => sortCountriesByName(partnerCountries(data), countryName, intlLocale(locale)),
    [data, countryName, locale],
  );

  const partner = data?.partner ?? null;

  return (
    <div className="space-y-6">
      {standalone && <PageHeader scope={t.scope} title={t.title} lede={t.lede} />}

      {state === 'loading' && <WaitingInline label={t.loading} />}
      {state === 'failed' && <div className="alert alert-error text-sm" role="alert">{t.loadError}</div>}

      {state === 'ready' && data && (
        <>
          {(!partner || partner.status === 'REJECTED') && (
            <>
              {partner?.status === 'REJECTED' && (
                <Card title={t.rejected.title}>
                  {partner.rejection_reason && (
                    <p className="text-sm">{t.rejected.reason(partner.rejection_reason)}</p>
                  )}
                  <p className="text-sm hush mt-1">{t.rejected.retry}</p>
                </Card>
              )}
              <HowItWorks />
              <ApplicationForm
                initial={initialForm(data)}
                email={partner?.email ?? data.prefill.email}
                countries={countries}
                countryName={countryName}
                onDone={() => void load()}
              />
            </>
          )}

          {partner?.status === 'PENDING' && (
            <Card title={t.pending.title}>
              <p className="text-sm">{t.pending.body(partner.code)}</p>
            </Card>
          )}

          {hasPartnerPanel(partner?.status) && (
            <>
              {needsPartnerTermsAcceptance(partner) && (
                <AcceptTermsCard version={partner!.terms!.version} onDone={() => void load()} />
              )}
              <PartnerPanel data={data} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function HowItWorks() {
  const t = useT('partner');
  return (
    <Card scope={t.how.scope}>
      <ul className="space-y-2 text-sm">
        <li>{t.how.discount}</li>
        <li>{t.how.commission}</li>
        <li>{t.how.payouts}</li>
        <li>
          <a href={t.how.terms.href} target="_blank" rel="noopener noreferrer" className="link">
            {t.how.terms.label}
          </a>
        </li>
      </ul>
    </Card>
  );
}

function ApplicationForm({
  initial,
  email,
  countries,
  countryName,
  onDone,
}: {
  initial: PartnerForm;
  email: string;
  countries: string[];
  countryName: (code: string) => string;
  onDone: () => void;
}) {
  const t = useT('partner');
  const [form, setForm] = useState<PartnerForm>(initial);
  const [bad, setBad] = useState<PartnerField[]>([]);
  const [error, setError] = useState<'codeTaken' | 'failed' | null>(null);
  const [sending, setSending] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsVersion, setTermsVersion] = useState(PARTNER_TERMS_VERSION);
  const [termsProblem, setTermsProblem] = useState<'required' | 'outdated' | null>(null);

  const set = (field: PartnerField) => (value: string) => {
    setForm((current) => ({ ...current, [field]: field === 'code' ? cleanPartnerCode(value) : value }));
    setBad((current) => current.filter((f) => f !== field));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const invalid = invalidPartnerFields(form, countries);
    setBad(invalid);
    if (!termsAccepted) setTermsProblem((current) => current ?? 'required');
    if (invalid.length || !termsAccepted) return;

    setSending(true);
    const result = await applyPartner(form, termsVersion);
    setSending(false);
    if (result.status === 'ok') { onDone(); return; }
    if (result.status === 'invalid') { setBad(result.fields); return; }
    if (result.status === 'terms') {
      // Los términos cambiaron (o no llegó la casilla): se vuelve a preguntar
      // con la versión que exige el servidor.
      if (result.error.current_version) setTermsVersion(result.error.current_version);
      setTermsAccepted(false);
      setTermsProblem(result.error.code === 'terms_outdated' ? 'outdated' : 'required');
      return;
    }
    setError(result.status === 'code_taken' ? 'codeTaken' : 'failed');
  };

  const fieldError = (field: PartnerField) => (bad.includes(field) ? t.form.errors[field] : null);

  return (
    <Card scope={t.form.scope} title={t.form.title}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)} noValidate>
        <Field id="partner-name" label={t.form.name} error={fieldError('name')}>
          <input id="partner-name" className="input input-bordered w-full" value={form.name} onChange={(e) => set('name')(e.target.value)} autoComplete="name" />
        </Field>

        <Field id="partner-email" label={t.form.email} hint={t.form.emailHint}>
          <input id="partner-email" className="input input-bordered w-full" value={email} readOnly />
        </Field>

        <Field id="partner-whatsapp" label={t.form.whatsapp} hint={t.form.whatsappHint} error={fieldError('whatsapp')}>
          <input
            id="partner-whatsapp"
            className="input input-bordered w-full"
            value={form.whatsapp}
            onChange={(e) => set('whatsapp')(e.target.value)}
            inputMode="tel"
            autoComplete="tel"
          />
        </Field>

        <Field id="partner-country" label={t.form.country} hint={t.form.countryHint} error={fieldError('country')}>
          <select id="partner-country" className="select select-bordered w-full" value={form.country} onChange={(e) => set('country')(e.target.value)}>
            <option value="">{t.form.countryPlaceholder}</option>
            {countries.map((code) => (
              <option key={code} value={code}>{countryName(code)}</option>
            ))}
          </select>
        </Field>

        {/* La lista a la vista y no sólo dentro del selector: quien vive en un
            país que no está tiene que enterarse ANTES de llenar todo, y con un
            camino ("escríbenos"), no con un selector donde su país no aparece. */}
        <div className="text-sm space-y-1">
          <p>
            <span className="font-medium">{t.form.countriesWePay}</span>{' '}
            {countries.map(countryName).join(', ')}.
          </p>
          <p className="hush">{t.form.countryMissing}</p>
        </div>

        <Field id="partner-community" label={t.form.community} error={fieldError('community_name')}>
          <input
            id="partner-community"
            className="input input-bordered w-full"
            value={form.community_name}
            placeholder={t.form.communityPlaceholder}
            onChange={(e) => set('community_name')(e.target.value)}
          />
        </Field>

        <Field id="partner-plan" label={t.form.plan} error={fieldError('promotion_plan')}>
          <textarea
            id="partner-plan"
            className="textarea textarea-bordered w-full"
            rows={3}
            value={form.promotion_plan}
            placeholder={t.form.planPlaceholder}
            onChange={(e) => set('promotion_plan')(e.target.value)}
          />
        </Field>

        <Field id="partner-code" label={t.form.code} hint={t.form.codeHint} error={fieldError('code')}>
          <input
            id="partner-code"
            className="input input-bordered w-full font-mono"
            value={form.code}
            onChange={(e) => set('code')(e.target.value)}
            autoCapitalize="characters"
          />
        </Field>

        {error && (
          <div className="alert alert-error text-sm" role="alert">
            {error === 'codeTaken' ? t.form.errors.codeTaken : t.form.errors.failed}
          </div>
        )}

        <TermsCheckbox
          id="partner-terms"
          checked={termsAccepted}
          onChange={(checked) => {
            setTermsAccepted(checked);
            if (checked) setTermsProblem(null);
          }}
          before={t.form.terms.before}
          label={t.form.terms.label}
          href={t.form.terms.href}
          after={t.form.terms.after}
          notice={termsProblem === 'outdated' ? t.form.terms.outdated : null}
          error={termsProblem === 'required' ? t.form.terms.required : null}
        />

        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? t.form.submitting : t.form.submit}
        </button>
      </form>
    </Card>
  );
}

/**
 * Un campo del formulario: la etiqueta (sólo el nombre, para que un lector de
 * pantalla lo lea limpio), el control y debajo la pista o, si falló, el error
 * en su lugar.
 */
function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="form-control w-full">
      <label htmlFor={id} className="label-text font-medium mb-1">{label}</label>
      {children}
      {error ? (
        <span className="label-text-alt text-friction mt-1">{error}</span>
      ) : hint ? (
        <span className="label-text-alt hush mt-1">{hint}</span>
      ) : null}
    </div>
  );
}

/**
 * El socio no aceptó la versión vigente de los términos (entró antes de la
 * casilla, o los términos cambiaron): se le pide aceptarla, con la casilla.
 * No bloquea el panel ni los cobros; lo que trae desde ahora entra con esta
 * versión, y sus referidos de antes conservan la suya.
 */
function AcceptTermsCard({ version: initialVersion, onDone }: { version: string; onDone: () => void }) {
  const t = useT('partner');
  const [version, setVersion] = useState(initialVersion);
  const [checked, setChecked] = useState(false);
  const [problem, setProblem] = useState<'required' | 'outdated' | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    setFailed(false);
    if (!checked) { setProblem('required'); return; }
    setSaving(true);
    const result = await acceptPartnerTerms(version);
    setSaving(false);
    if (result.status === 'ok') { onDone(); return; }
    if (result.status === 'terms') {
      if (result.error.current_version) setVersion(result.error.current_version);
      setChecked(false);
      setProblem(result.error.code === 'terms_outdated' ? 'outdated' : 'required');
      return;
    }
    setFailed(true);
  };

  return (
    <Card title={t.panel.terms.title(version)}>
      <p className="text-sm mb-3">{t.panel.terms.body}</p>
      <TermsCheckbox
        id="partner-terms-reaccept"
        checked={checked}
        onChange={(value) => {
          setChecked(value);
          if (value) setProblem(null);
        }}
        before={t.form.terms.before}
        label={t.form.terms.label}
        href={t.form.terms.href}
        after={t.form.terms.after}
        notice={problem === 'outdated' ? t.form.terms.outdated : null}
        error={problem === 'required' ? t.form.terms.required : null}
      />
      <button type="button" className="btn btn-primary btn-sm mt-3" disabled={saving} onClick={() => void accept()}>
        {saving ? t.panel.terms.accepting : t.panel.terms.accept}
      </button>
      {failed && <div className="alert alert-error text-sm mt-2" role="alert">{t.panel.terms.failed}</div>}
    </Card>
  );
}

function PartnerPanel({ data }: { data: PartnerResponse }) {
  const t = useT('partner');
  const partner = data.partner!;
  const commissions = data.commissions ?? [];
  const totals = data.totals ?? { pending_cents: 0, paid_cents: 0, failed_cents: 0 };
  const [copied, setCopied] = useState(false);
  const [opening, setOpening] = useState(false);
  const [payoutErr, setPayoutErr] = useState<'failed' | 'unsupportedCountry' | null>(null);

  const copy = async () => {
    if (!partner.link) return;
    try {
      await navigator.clipboard.writeText(partner.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Sin permiso de portapapeles el enlace sigue a la vista para copiarlo a mano.
    }
  };

  const openPayouts = async () => {
    setPayoutErr(null);
    setOpening(true);
    const result = await requestPayoutsLink();
    if (result.status === 'ok') {
      // El enlace de Stripe caduca en minutos: se abre ya, no se guarda.
      window.location.assign(result.url);
      return;
    }
    setOpening(false);
    setPayoutErr(result.status === 'unsupported_country' ? 'unsupportedCountry' : 'failed');
  };

  const payoutLabel =
    partner.payouts === 'ready'
      ? t.panel.payouts.dashboard
      : partner.payouts === 'pending'
        ? t.panel.payouts.continue
        : t.panel.payouts.setup;

  return (
    <div className="space-y-6">
      {partner.status === 'PAUSED' && (
        <div className="alert alert-warning text-sm" role="status">{t.panel.paused}</div>
      )}
      {partner.status === 'TERMINATED' && (
        <div className="alert alert-warning text-sm" role="status">{t.panel.terminated}</div>
      )}
      {partner.payouts_held && (
        <div className="alert alert-warning text-sm" role="status">{t.panel.payoutsHeld}</div>
      )}

      <Card scope={t.panel.codeScope} title={<span className="font-mono">{partner.code}</span>}>
        {partner.link && (
          <div className="space-y-2">
            <span className="label-text font-medium">{t.panel.linkLabel}</span>
            <div className="flex gap-2 flex-wrap items-center">
              <code className="text-sm break-all">{partner.link}</code>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => void copy()}>
                {copied ? t.panel.copied : t.panel.copy}
              </button>
            </div>
            <p className="text-sm hush">{t.panel.shareNote}</p>
          </div>
        )}
      </Card>

      <Card
        scope={t.panel.payouts.scope}
        trail={
          <Tone tone={partner.payouts === 'ready' ? 'good' : partner.payouts === 'pending' ? 'warn' : 'muted'}>
            {t.panel.payouts.status[partner.payouts]}
          </Tone>
        }
      >
        <p className="text-sm">{t.panel.payouts[partner.payouts]}</p>
        <button
          type="button"
          className={`btn btn-sm mt-3 ${partner.payouts === 'ready' ? 'btn-ghost' : 'btn-primary'}`}
          disabled={opening}
          onClick={() => void openPayouts()}
        >
          {opening ? t.panel.payouts.opening : payoutLabel}
        </button>
        {payoutErr && (
          <div className="alert alert-error text-sm mt-2" role="alert">
            {payoutErr === 'unsupportedCountry' ? t.panel.payouts.unsupportedCountry : t.panel.payouts.failed}
          </div>
        )}
      </Card>

      <div className="stats stats-vertical sm:stats-horizontal w-full">
        {/* Mint sólo para lo pagado: es la única cifra que ya salió de la cuenta. */}
        <Stat title={t.panel.totals.paid} value={formatUsdCents(totals.paid_cents)} tone="data" />
        <Stat title={t.panel.totals.pending} value={formatUsdCents(totals.pending_cents)} />
        {totals.failed_cents > 0 && (
          <Stat title={t.panel.totals.failed} value={formatUsdCents(totals.failed_cents)} tone="friction" />
        )}
      </div>

      {/* Socio en pausa o terminado: el servidor no manda la lista (ni los correos). */}
      {data.referrals ? (
        <ReferralsCard referrals={data.referrals} summary={data.referral_summary ?? null} />
      ) : (
        <Card scope={t.panel.referrals.scope}>
          <p className="text-sm hush">{t.panel.referrals.hidden}</p>
        </Card>
      )}

      <Card scope={t.panel.commissions.scope}>
        {commissions.length === 0 ? (
          <p className="text-sm hush">{t.panel.commissions.empty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead><tr>
                <th>{t.panel.commissions.columns.date}</th>
                <th>{t.panel.commissions.columns.amount}</th>
                <th>{t.panel.commissions.columns.status}</th>
              </tr></thead>
              <tbody>
                {commissions.map((c, i) => (
                  <tr key={`${c.date}-${i}`}>
                    <td className="text-xs">{formatDate(c.date)}</td>
                    <td>{formatUsdCents(c.amount_cents)}</td>
                    <td className="text-xs">
                      {c.status === 'VOID' && c.void_reason
                        ? t.panel.commissions.voidReason[c.void_reason] ?? t.panel.commissions.status.VOID
                        : t.panel.commissions.status[c.status]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** El color de cada estado. Mint sólo para «Pagando»: es lo único que ya da plata. */
const STATE_TONE: Record<ReferralState, 'good' | 'info' | 'warn' | 'muted'> = {
  paying: 'good',
  trial: 'info',
  no_trial: 'warn',
  canceled: 'muted',
  courtesy: 'muted',
};

/**
 * Los referidos del socio, para que les haga seguimiento: arriba cuántos se
 * registraron y cuántos hay en cada estado (cada cifra es además el filtro), y
 * abajo la lista con el correo completo, el más reciente primero.
 *
 * «Sin prueba activa» va en ámbar y no apagado: es la gente a la que el socio
 * todavía puede ayudar a arrancar, que es para lo que existe esta lista.
 */
function ReferralsCard({ referrals, summary }: { referrals: PartnerReferral[]; summary: ReferralSummary | null }) {
  const t = useT('partner');
  const [filter, setFilter] = useState<ReferralFilter>('all');
  const counts = useMemo(() => summarizeReferrals(referrals, summary), [referrals, summary]);
  const shown = useMemo(() => visibleReferrals(referrals, filter), [referrals, filter]);
  const tr = t.panel.referrals;
  // «Cortesía» sólo aparece si hay alguna: es raro, y un filtro en cero para
  // algo que casi nunca pasa sólo hace ruido.
  const states = REFERRAL_STATES.filter((s) => s !== 'courtesy' || counts.courtesy > 0);
  // La columna del contrato sólo con un backend que la manda.
  const showTerms = referrals.some((r) => Boolean(r.terms_version));

  return (
    <Card scope={tr.scope} title={tr.registered(counts.total)}>
      {counts.total === 0 ? (
        <EmptyState icon="users" title={tr.empty} note={tr.emptyNote} />
      ) : (
        <div className="space-y-4">
          <p className="text-sm hush">{tr.followUp}</p>

          <div className="flex flex-wrap gap-2" role="group" aria-label={tr.filterLabel}>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label={tr.all} count={counts.total} />
            {states.map((state) => (
              <FilterChip
                key={state}
                active={filter === state}
                onClick={() => setFilter(state)}
                label={tr.state[state]}
                count={counts[state]}
              />
            ))}
          </div>

          {shown.length === 0 ? (
            <p className="text-sm hush">{tr.emptyFilter}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead><tr>
                  <th>{tr.columns.name}</th>
                  <th>{tr.columns.email}</th>
                  <th>{tr.columns.registered}</th>
                  <th>{tr.columns.state}</th>
                  {showTerms && <th>{tr.columns.terms}</th>}
                </tr></thead>
                <tbody>
                  {shown.map((r, i) => (
                    <tr key={`${r.email}-${i}`}>
                      <td>{r.name ?? ''}</td>
                      <td className="text-xs break-all">
                        <a className="link" href={`mailto:${r.email}`}>{r.email}</a>
                      </td>
                      <td className="text-xs">{formatDate(r.registered_at ?? r.referred_at)}</td>
                      <td>
                        <Tone tone={STATE_TONE[r.state] ?? 'muted'}>{tr.state[r.state] ?? r.state}</Tone>
                        {r.state === 'trial' && r.trial_ends_at && (
                          <span className="block text-xs hush mt-1">{tr.trialEnds(formatDate(r.trial_ends_at))}</span>
                        )}
                      </td>
                      {showTerms && (
                        <td className="text-xs hush whitespace-nowrap">
                          {r.terms_version ? tr.termsVersion(r.terms_version) : ''}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {counts.courtesy > 0 && <p className="text-xs hush">{tr.courtesyNote}</p>}
          {showTerms && <p className="text-xs hush">{tr.termsNote}</p>}
        </div>
      )}
    </Card>
  );
}

/** Un estado con su cifra, que además filtra la lista. */
function FilterChip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-ghost'}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {label} <span className="font-semibold">{count}</span>
    </button>
  );
}
